import { Button } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import {
    AcceptAll,
    AcceptedChanged,
    CanSaveApprovedDocument,
    FinaliseReviewUpdates,
    IsAccepted,
    SendReviewUpdate,
} from "event-definitions"
import { splitId } from "library/database/split-id.mjs"
import { useEffect, useRef, useState } from "react"
import { omit } from "lib/omit"
import { navigate } from "lib/routes/navigate"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { noChange } from "lib/@hooks/useRefresh"
import { SaveButtonTop } from "slot-definitions"
import { updateAudit } from "lib/@offline/queued-audits"
import { mapAsync } from "library/map-async.mjs"

export function SaveButton({ bottom }) {
    const {
        canSave,
        target,
        comparisons,
        comparisonReadOnly,
        save,
        saveFunction,
        commitFunctions,
        saveButtonCaption = "Save",
    } = useBoundContext()
    const [accept, setAccept] = useState({})
    const [registered, setRegistered] = useState({})
    const queueRef = useRef([])
    const processingRef = useRef(Promise.resolve())
    AcceptedChanged.after.useRefresh(noChange)
    AcceptedChanged.useEvent(({ id, accepted = null, uniqueId }) => {
        if (!id) return
        setAccept((accept) => {
            if (accepted === undefined) {
                if (registered[id] === uniqueId) {
                    return omit(accept, id)
                }
                return accept
            }
            setRegistered((r) => ({ ...r, [id]: uniqueId }))
            return { ...accept, [id]: accepted }
        })
    })
    IsAccepted.useEvent(
        (props) => {
            props.isAccepted = accept[props.id]
        },
        [accept]
    )
    SendReviewUpdate.useEvent(async ({ payload, auditId }) => {
        if (!payload) return
        queueRef.current.push({ payload: payload.payload, referenceId: payload.referenceId, auditId })
        approveAllAudits()
    })
    FinaliseReviewUpdates.useEvent(async () => {
        await approveAllAudits()
    })

    const isAccepted = Object.values(accept).every(Boolean)
    useEffect(() => {
        CanSaveApprovedDocument.raise(isAccepted)
    })

    const someAccepted = Object.values(accept).some(Boolean)
    const saveFn = saveFunction ?? doSave

    if (comparisonReadOnly) {
        return null
    }

    return (
        (!bottom || comparisons.length > 4) &&
        !!canSave && (
            <>
                <SaveButtonTop.Slot>
                    <Button
                        size="small"
                        disabled={someAccepted}
                        onClick={rejectAll}
                        variant="outlined"
                        color="error"
                        startIcon={<Iconify icon="ic:round-clear" />}
                    >
                        Reject All
                    </Button>
                </SaveButtonTop.Slot>
                <Button
                    size="small"
                    disabled={isAccepted}
                    onClick={acceptAll}
                    variant="outlined"
                    color="success"
                    startIcon={<Iconify icon="akar-icons:double-check" />}
                    data-cy="approve-all-button"
                >
                    Approve All
                </Button>
                <Button
                    size="small"
                    onClick={saveFn}
                    variant="contained"
                    color="secondary"
                    disabled={!isAccepted}
                    data-cy="save-btn"
                >
                    {saveButtonCaption}
                </Button>
            </>
        )
    )

    async function doSave() {
        for (const fn of commitFunctions.values()) {
            fn(target)
        }
        try {
            await save(target)
        } catch (e) {
            console.error(e)
        } finally {
            navigate(`/app/schedules/${splitId(target._id).id}/edit`, { replace: true })
        }
    }

    function acceptAll() {
        AcceptAll.raise(true)
    }

    function rejectAll() {
        AcceptAll.raise(false, accept, someAccepted, isAccepted)
    }

    function approveAllAudits() {
        processingRef.current = processingRef.current.then(async () => {
            const queue = queueRef.current.splice(0)
            if (!queue.length) return

            await mapAsync(queue, async ({ payload, referenceId, auditId }) =>
                updateAudit({
                    auditId,
                    type: "customSchedule",
                    referenceId,
                    shortDescription: payload.shortDescription,
                    longDescription: payload.longDescription ?? payload.shortDescription,
                    payload,
                })
            )
        })
        return processingRef.current
    }
}
